import React, { useEffect, useRef } from 'react';
import './Games.css';
import { initWOW } from '../../utils/animations';

const Games = () => {
  const videoRef = useRef(null);

  useEffect(() => {
    initWOW();
    document.body.classList.add('games-page-active');
    return () => {
      document.body.classList.remove('games-page-active');
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      // Set playback quality settings for smooth playback
      video.defaultPlaybackRate = 1.0;
      video.playbackRate = 1.0;
      
      // Increase buffer size for smoother playback
      if (video.buffered && video.buffered.length > 0) {
        // Wait for more data to buffer before playing
        video.addEventListener('progress', () => {
          if (video.buffered.length > 0) {
            const bufferedEnd = video.buffered.end(video.buffered.length - 1);
            const currentTime = video.currentTime;
            // If we have at least 3 seconds buffered ahead, we're good
            if (bufferedEnd - currentTime > 3 && video.paused) {
              video.play().catch(() => {});
            }
          }
        }, { once: false });
      }
      
      // Force load video with metadata
      video.load();
      
      // Function to play video
      const playVideo = async () => {
        try {
          if (video.paused) {
            await video.play();
          }
        } catch (error) {
          console.error('Error playing video:', error);
        }
      };
      
      // Handle waiting event (when video stalls)
      const handleWaiting = () => {
        // Pause briefly to let buffer catch up
        if (!video.paused) {
          video.pause();
          setTimeout(() => {
            if (video.readyState >= 3) {
              video.play().catch(() => {});
            }
          }, 100);
        }
      };
      
      // Handle playing event (when video resumes)
      const handlePlaying = () => {
        // Video is playing smoothly
      };
      
      video.addEventListener('waiting', handleWaiting);
      video.addEventListener('playing', handlePlaying);
      
      // Try to play when video is ready
      const tryPlayWhenReady = () => {
        if (video.readyState >= 4) { // HAVE_ENOUGH_DATA
          playVideo();
          return true;
        }
        return false;
      };
      
      // Listen for events - wait for enough data
      const handleCanPlayThrough = () => {
        playVideo();
      };
      
      video.addEventListener('canplaythrough', handleCanPlayThrough, { once: true });
      
      // Try immediately if already loaded
      let timeoutId;
      if (!tryPlayWhenReady()) {
        // Also try after a delay
        timeoutId = setTimeout(playVideo, 500);
      }
      
      // Error handler
      video.addEventListener('error', (e) => {
        const error = video.error;
        if (error) {
          console.error('Video error:', error.code, error.message);
        }
      });
      
      return () => {
        // Cleanup
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        video.removeEventListener('waiting', handleWaiting);
        video.removeEventListener('playing', handlePlaying);
        video.removeEventListener('canplaythrough', handleCanPlayThrough);
        if (video) {
          video.pause();
        }
      };
    }
  }, []);

  const gameServices = [
    {
      title: 'UE5 MULTIPLAYER EXPERTISE',
      description: 'From gameplay systems to network architecture, we solve the technical challenges that slow projects down'
    },
    {
      title: 'VR PORTING & OPTIMIZATION',
      description: 'Smooth performance, intuitive interaction, and real presence across all major VR platforms.'
    },
    {
      title: 'REAL-TIME & TECHNICAL ART',
      description: 'Characters, environments, shaders, VFX — built to look great and run efficiently in real-time.'
    },
    {
      title: 'FULL-CYCLE PRODUCTION',
      description: 'We join your team or take the lead — from concept to launch — ensuring stable delivery and clear communication.'
    }
  ];

  const selectedWork = [
    {
      title: 'STYLIZED CHARACTERS',
      description: 'Expressive, clean, animation-ready heroes built to define the look of your game.',
      image: '/images/Fighters.jpg'
    },
    {
      title: 'STYLIZED WEAPONS',
      description: 'Bold shapes, readable silhouettes, and performance-friendly detail for gameplay clarity.',
      image: '/images/weapon.jpg'
    },
    {
      title: 'REALISTIC WEAPONS',
      description: 'High-fidelity models with precise materials and wear — ready for close-up shots.',
      image: '/images/realistic_weapon_1.jpg'
    },
    {
      title: 'REALISTIC CHARACTERS',
      description: 'Believable human characters focused on anatomy, proportions, and performance-ready rigs',
      image: '/images/gemini-image_3.jpg'
    }
  ];

  return (
    <div className="games-page">
      {/* 1. HERO SECTION */}
      <div id="games-hero" className="container-fluid pl-0 pr-0 clearfix" 
           style={{minHeight: '100vh', display: 'flex', flexDirection: 'column', position: 'relative', background: 'transparent'}}>
        <video 
          ref={videoRef}
          autoPlay 
          loop 
          muted 
          playsInline
          preload="auto"
          webkit-playsinline="true"
          onError={(e) => {
            const error = e.target.error;
            if (error) {
              console.error('Video error:', error.code, error.message);
            }
          }}
          onCanPlay={() => {
            const video = videoRef.current;
            if (video) {
              video.play().catch(err => console.error('Play error:', err));
            }
          }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
            backgroundColor: '#000',
            pointerEvents: 'none'
          }}
        >
          <source src={`${process.env.PUBLIC_URL || ''}/videos/Trailer_for_Site.webm`} type="video/webm" />
          <source src="/videos/Trailer_for_Site.webm" type="video/webm" />
          <source src={`${process.env.PUBLIC_URL || ''}/videos/Trailer_for_Site.mp4`} type="video/mp4" />
          <source src="/videos/Trailer_for_Site.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="container-fluid position-relative px-5" style={{flex: 1, minHeight: 'calc(100vh - 80px)', zIndex: 1}}>
          <div className="position-absolute" style={{left: '3rem', bottom: '100px', maxWidth: '900px', zIndex: 2}}>
            <div className="wow bounceInRight">
              <h3 className="text-white text-uppercase mb-3">
                Game development support for teams and creators who need a trusted, high-skill production partner.
              </h3>
              <p className="lead text-white mb-4 d-none d-md-block"></p>
              <a 
                href="#contact" 
                className="btn text-uppercase btn-lg wow bounceInUp btn-demo"
                  style={{
                    backgroundColor: '#f85aff',
                    borderColor: '#f85aff',
                    color: '#fff',
                    borderRadius: 0
                  }}
              >
                Start a conversation
              </a>
            </div>
          </div>
          <div className="position-absolute text-right" style={{right: '3rem', top: '100px', maxWidth: '780px', zIndex: 2}}>
            <div className="wow bounceInLeft">
              <p className="lead text-white text-uppercase mb-0">
                We cover art, engineering, VR, multiplayer, and UE5 — taking on complex tasks, reinforcing your pipeline, and helping you ship the game you want to build.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 2. GAME PRODUCTION SERVICES */}
      <div id="game-production-services" className="container-fluid fh5co-network">
        <div className="container-fluid px-5">
          <div className="row">
            <div className="col-12">
              <div className="quote-box2 wow bounceInDown" data-wow-delay=".15s">
                <h2 className="games-gradient-text">GAME PRODUCTION SERVICES</h2>
              </div>
            </div>
          </div>
          <div className="row align-items-stretch" style={{marginTop: '3.15rem'}}>
            {gameServices.map((service, index) => (
              <div key={index} className="col-md-3">
                <div 
                  className={`card h-100 wow ${index === 0 ? 'bounceInLeft' : index === 1 ? 'bounceInUp' : index === 2 ? 'bounceInUp' : 'bounceInRight'}`} 
                  data-wow-delay={`${0.1 + index * 0.05}s`}
                  style={{
                    borderRadius: 0,
                    position: 'relative',
                    overflow: 'hidden',
                    padding: 0,
                    border: 'none'
                  }}
                >
                  <div className="card-body d-flex flex-column">
                    <h4 className="card-title">{service.title}</h4>
                    <p className="card-text">{service.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 3. BOSS FIGHTERS SHOWCASE */}
      <div id="boss-fighters-showcase" className="container-fluid fh5co-content-box" style={{backgroundColor: '#111111', paddingTop: '3.15rem', paddingBottom: '3.15rem'}}>
        <div className="container-fluid px-5">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="video-container" style={{
                aspectRatio: '16/9',
                width: '100%',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/XEPdv28_as4"
                  title="Boss Fighters Showcase"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%'
                  }}
                ></iframe>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="showcase-content">
                <h2 className="wow bounceInRight">
                  BOSS FIGHTERS SHOWCASE
                </h2>
                <p className="wow bounceInLeft text-white mb-3">
                  A full-cycle production case that proves our ability to handle complex, multi-platform game development.
                </p>
                <p className="wow bounceInLeft text-white mb-3">
                  We built the project from concept to release: art, engineering, VR+PC asymmetric gameplay, networking, optimization, and live-ops support.
                </p>
                <p className="wow bounceInLeft text-white mb-3">
                  <strong>Result:</strong> a stable, visually strong, technically ambitious title that showcases our multiplayer expertise, UE5 engineering depth, and ability to manage a complete production pipeline.
                </p>
                <div className="wow bounceInLeft" style={{marginTop: '1.5rem'}}>
                  <a 
                    href="https://store.steampowered.com/app/1784650/BOSS_FIGHTERS/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn text-uppercase btn-lg games-gradient-button"
                    style={{
                      backgroundColor: '#f85aff',
                      borderColor: '#f85aff',
                      color: '#fff',
                      borderRadius: 0,
                      padding: '0.75rem 1.5rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      textDecoration: 'none'
                    }}
                  >
                    <img 
                      src="/images/steam.svg" 
                      alt="Steam" 
                      style={{
                        width: '20px',
                        height: '20px',
                        marginRight: '10px',
                        filter: 'brightness(0) invert(1)'
                      }}
                    />
                    Play On Steam
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 4. SELECTED GAME WORK */}
      <div className="container-fluid fh5co-content-box" id="selected-game-work-wrapper" style={{backgroundColor: '#111111'}}>
        <div className="container-fluid px-5">
          <div id="selected-game-work">
            <div className="row">
              <div className="col-12">
                <div className="quote-box2 wow bounceInDown" data-wow-delay=".15s">
                  <h2 className="games-gradient-text">SELECTED GAME WORK</h2>
                </div>
              </div>
            </div>
            {/* First row with 2 cards */}
            <div className="row align-items-start">
              {selectedWork.slice(0, 2).map((work, index) => (
                <div key={index} className="col-lg-6 d-flex flex-column">
                  {index === 0 ? (
                    <>
                      <a 
                        href="https://www.artstation.com/artwork/b5kOzd" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        style={{ display: 'block', textDecoration: 'none' }}
                      >
                      <div 
                        className="game-work-item position-relative wow bounceInUp" 
                        data-wow-delay={`${0.1 + index * 0.05}s`}
                        style={{overflow: 'hidden'}}
                      >
                        <div 
                          className="game-work-media w-100 h-100" 
                          style={{
                            backgroundColor: '#000',
                            width: '100%',
                            height: '100%',
                            backgroundImage: work.image ? `url('${work.image}')` : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                          }}
                        ></div>
                          <div 
                            className="position-absolute w-100 h-100" 
                            style={{
                              bottom: 0,
                              left: 0,
                              background: 'linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, transparent 40%)',
                              pointerEvents: 'none',
                              zIndex: 1
                            }}
                          ></div>
                          <div className="position-absolute" style={{bottom: 0, left: '40px', right: '40px', paddingBottom: '30px', zIndex: 2}}>
                          <h3 className="text-white mb-0" style={{margin: 0, padding: 0, lineHeight: '1.2'}}>{work.title}</h3>
                        </div>
                      </div>
                      </a>
                      <div className="game-work-text-wrapper">
                        <div className="game-work-text-content">
                          <p className="mb-0">{work.description}</p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="game-work-text-wrapper">
                        <div className="game-work-text-content">
                          <p className="mb-0">{work.description}</p>
                        </div>
                      </div>
                      <a 
                        href="https://www.artstation.com/pixwardstudio" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        style={{ display: 'block', textDecoration: 'none' }}
                      >
                      <div 
                        className="game-work-item position-relative wow bounceInUp" 
                        data-wow-delay={`${0.1 + index * 0.05}s`}
                        style={{overflow: 'hidden'}}
                      >
                        <div 
                          className="game-work-media w-100 h-100" 
                          style={{
                            backgroundColor: '#000',
                            width: '100%',
                            height: '100%',
                            backgroundImage: work.image ? `url('${work.image}')` : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                          }}
                        ></div>
                          <div 
                            className="position-absolute w-100 h-100" 
                            style={{
                              top: 0,
                              left: 0,
                              background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.6) 0%, transparent 40%)',
                              pointerEvents: 'none',
                              zIndex: 1
                            }}
                          ></div>
                          <div className="position-absolute" style={{top: 0, left: '40px', right: '40px', paddingTop: '30px', zIndex: 2}}>
                          <h3 className="text-white mb-0" style={{margin: 0, padding: 0, lineHeight: '1.2'}}>{work.title}</h3>
                        </div>
                      </div>
                      </a>
                    </>
                  )}
                </div>
              ))}
            </div>
            {/* Second row with 2 cards */}
            <div className="row align-items-start" style={{marginTop: '1rem'}}>
              {selectedWork.slice(2, 4).map((work, index) => (
                <div key={index + 2} className="col-lg-6 d-flex flex-column">
                  {index === 0 ? (
                    <>
                      <a 
                        href="https://www.artstation.com/pixwardstudio" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        style={{ display: 'block', textDecoration: 'none' }}
                      >
                      <div 
                        className="game-work-item position-relative wow bounceInUp" 
                        data-wow-delay={`${0.3 + index * 0.05}s`}
                        style={{overflow: 'hidden'}}
                      >
                        <div 
                          className="game-work-media w-100 h-100" 
                          style={{
                            backgroundColor: '#000',
                            width: '100%',
                            height: '100%',
                            backgroundImage: work.image ? `url('${work.image}')` : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                          }}
                        ></div>
                          <div 
                            className="position-absolute w-100 h-100" 
                            style={{
                              bottom: 0,
                              left: 0,
                              background: 'linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, transparent 40%)',
                              pointerEvents: 'none',
                              zIndex: 1
                            }}
                          ></div>
                          <div className="position-absolute" style={{bottom: 0, left: '40px', right: '40px', paddingBottom: '30px', zIndex: 2}}>
                          <h3 className="text-white mb-0" style={{margin: 0, padding: 0, lineHeight: '1.2'}}>{work.title}</h3>
                        </div>
                      </div>
                      </a>
                      <div className="game-work-text-wrapper">
                        <div className="game-work-text-content">
                          <p className="mb-0">{work.description}</p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="game-work-text-wrapper">
                        <div className="game-work-text-content">
                          <p className="mb-0">{work.description}</p>
                        </div>
                      </div>
                      <a 
                        href="https://www.artstation.com/pixwardstudio" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        style={{ display: 'block', textDecoration: 'none' }}
                      >
                      <div 
                        className="game-work-item position-relative wow bounceInUp" 
                        data-wow-delay={`${0.3 + index * 0.05}s`}
                        style={{overflow: 'hidden'}}
                      >
                        <div 
                          className="game-work-media w-100 h-100" 
                          style={{
                            backgroundColor: '#000',
                            width: '100%',
                            height: '100%',
                            backgroundImage: work.image ? `url('${work.image}')` : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                          }}
                        ></div>
                          <div 
                            className="position-absolute w-100 h-100" 
                            style={{
                              top: 0,
                              left: 0,
                              background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.6) 0%, transparent 40%)',
                              pointerEvents: 'none',
                              zIndex: 1
                            }}
                          ></div>
                          <div className="position-absolute" style={{top: 0, left: '40px', right: '40px', paddingTop: '30px', zIndex: 2}}>
                          <h3 className="text-white mb-0" style={{margin: 0, padding: 0, lineHeight: '1.2'}}>{work.title}</h3>
                        </div>
                      </div>
                      </a>
                    </>
                  )}
                </div>
              ))}
            </div>
            {/* VIEW PORTFOLIO BUTTON */}
            <div className="row" style={{marginTop: '3.15rem', marginBottom: '3.15rem', position: 'relative', zIndex: 10}}>
              <div className="col-12 text-center">
                <a 
                  href="https://www.artstation.com/pixwardstudio" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn text-uppercase btn-lg wow bounceInUp btn-demo games-gradient-button"
                  style={{
                    backgroundColor: '#f85aff',
                    borderColor: '#f85aff',
                    color: '#fff',
                    borderRadius: 0,
                    position: 'relative',
                    zIndex: 10
                  }}
                >
                  View Full Portfolio
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Games;
